import type { TJohnDeereImportStatus } from "@services/devicesService";

/**
 * How Analytics labels a John Deere combine (devices.source = "johndeere").
 *
 * "We trust Deere" (2026-10-20): without a GreenGrowth calibration a Deere
 * combine's mass is Deere's own wet kg (devices.default_coefficient = 100), so
 * it is labelled "John Deere" — never "uncalibrated" or "estimated". The
 * backend reports that as the coefficient source "johndeere" on the dashboard
 * (YieldDevice.calCoeffSource) and on a weight calculation
 * (WeightCalculationResult.calCoeffSource).
 */

/** The coefficient source the backend sends for an uncalibrated Deere combine. */
export const JOHN_DEERE_COEFF_SOURCE = "johndeere";

/** Takes any string, so a raw source from either service type can be passed. */
export const isJohnDeereCoeffSource = (source: string | null | undefined): boolean =>
  source === JOHN_DEERE_COEFF_SOURCE;

/**
 * Whether a window ends after the last Deere import, i.e. harvest at its end
 * may not be in this app yet. Deere data arrives in sync batches every few
 * hours and an import can rewrite a window, so a weight saved soon after the
 * work is an undercount until the next import. A combine never imported
 * counts as behind; null (our own hardware, or no status) never does.
 */
export function jdWindowAfterImport(
  windowEnd: Date,
  status: TJohnDeereImportStatus | null | undefined
): boolean {
  if (!status) return false;
  if (!status.lastImportAt) return true;
  const last = new Date(status.lastImportAt).getTime();
  return Number.isNaN(last) || windowEnd.getTime() > last;
}
